import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import { useApp } from 'store/steps'
import { FormattedMessage } from 'react-intl'
import StepHeading from 'components/global/StepHeading'
import { useCart } from 'store/cart-slice/hooks'
import { Col, Row } from 'styled-bootstrap-grid'
import { useAppSelector } from 'store/hooks'
import { useEffect, useState } from 'react'
import {
    calculateTicketsAddToCartEvent,
    calculateUpsellsAddToCartEvent,
    pushDataLayer,
} from 'helpers/analytics'
import steps from 'index'

interface StepProps {
    children: React.ReactNode
    title: string
    isButtonDisabled?: boolean
    onButtonClick?: () => Promise<void>
    onSkipButtonClick?: () => Promise<void>
    isButtonVisible?: boolean
    isButtonLoading?: boolean
    isNarrow?: boolean
    withSkipToSummaryButton?: boolean
    withGoingBackDisabled?: boolean
}

const Step = ({
    title,
    children,
    onButtonClick,
    onSkipButtonClick,
    isButtonDisabled = false,
    isButtonVisible = true,
    isButtonLoading,
    isNarrow,
    withSkipToSummaryButton,
    withGoingBackDisabled,
}: StepProps) => {
    const { stepIndex, navigateToStep, stepId, isCreatorAccommodation } =
        useApp()
    const { isCartFetching } = useCart()
    const { areTicketsUpdating } = useAppSelector((state) => state.ticket)
    const { tickets, ticketsBundles, spa, busTickets, cabanas } =
        useAppSelector((state) => state.cart.items)

    const [shouldRedirect, setShouldRedirect] = useState(false)
    const [activeButton, setActiveButton] = useState<
        'continue' | 'skip' | null
    >(null)

    const shouldButtonBeDisabled =
        isButtonDisabled || isCartFetching || areTicketsUpdating

    const shouldButtonBeLoading =
        isButtonLoading || isCartFetching || areTicketsUpdating

    useEffect(() => {
        if (isCartFetching || !shouldRedirect) return
        setShouldRedirect(false)
        goToNextStep()
    }, [isCartFetching])

    const goToNextStep = () => {
        const nextStepId = steps[stepIndex + 1].id
        const isNextStepUpsells = nextStepId === 'upsell'

        // < analytics
        const isTicketsStep = stepId === 'tickets'
        const isUpsellsStep = stepId === 'upsell'
        const isAccommodationStep = stepId === 'accommodation_details'
        const isCardOrderStep = stepId === 'card_summary'
        const isRenewOrderStep = stepId === 'renew_summary'

        if (isTicketsStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: !isCreatorAccommodation
                    ? 'tickets'
                    : 'accommodation',
                event_action: 'tickets_params_selected',
                event_label: '',
            })
            if (tickets.length || ticketsBundles.length) {
                const ticketsAddToCartEvent = calculateTicketsAddToCartEvent([
                    ...ticketsBundles,
                    ...tickets,
                ])
                pushDataLayer(ticketsAddToCartEvent)
            }
        }

        if (isUpsellsStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: !isCreatorAccommodation
                    ? 'tickets'
                    : 'accommodation',
                event_action: 'extra_services_selected',
                event_label: '',
            })
            if (spa.length || busTickets.length || cabanas.length) {
                const upsellsAddToCartEvent = calculateUpsellsAddToCartEvent(
                    spa,
                    busTickets,
                    cabanas
                )
                pushDataLayer(upsellsAddToCartEvent)
            }
        }

        if (isAccommodationStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: !isCreatorAccommodation
                    ? 'tickets'
                    : 'accommodation',
                event_action: 'accommodation_params_selected',
                event_label: '',
            })
        }

        if (isCardOrderStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'unlimited',
                event_action: 'unlimited_params_confirmed',
                event_label: '',
            })
        }

        if (isRenewOrderStep) {
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'unlimited_renew',
                event_action: 'unlimited_params_confirmed',
                event_label: '',
            })
        }
        // analytics >

        navigateToStep({
            stepId: nextStepId,
            withoutScrollAnimation: isNextStepUpsells,
        })
    }

    const handleContinue = () => {
        setActiveButton('continue')
        onButtonClick
            ? onButtonClick()
                  .then(() => setShouldRedirect(true))
                  .catch((error) => {
                      setActiveButton(null)
                      console.error(error)
                  })
            : goToNextStep()
    }

    const handleSkipToSummary = () => {
        setActiveButton('skip')
        if (onSkipButtonClick) {
            onSkipButtonClick()
                .then(() => navigateToStep({ stepId: 'order' }))
                .catch((error) => {
                    setActiveButton(null)
                    console.error(error)
                })
        } else {
            // < analytics
            const isUpsellsStep = stepId === 'upsell'

            if (isUpsellsStep) {
                pushDataLayer({
                    event: 'funnelStep',
                    event_category: !isCreatorAccommodation
                        ? 'tickets'
                        : 'accommodation',
                    event_action: 'extra_services_selected',
                    event_label: '',
                })
                if (spa.length || busTickets.length || cabanas.length) {
                    const upsellsAddToCartEvent =
                        calculateUpsellsAddToCartEvent(spa, busTickets, cabanas)
                    pushDataLayer(upsellsAddToCartEvent)
                }
            }
            // analytics >
            navigateToStep({ stepId: 'order' })
        }
    }

    return (
        <Row>
            <Col lg={isNarrow ? 10 : 12} lgOffset={isNarrow ? 1 : 0}>
                <Box gap={[16, 24]} pt={title ? 0 : [12, 16]} align="stretch">
                    {!!title && (
                        <StepHeading
                            title={title}
                            withGoingBackDisabled={withGoingBackDisabled}
                        />
                    )}

                    {children}

                    {isButtonVisible && (
                        <Box
                            direction={['column-reverse', 'row']}
                            justify="flex-end"
                            align="center"
                            mt={24}
                            gap={16}
                        >
                            {withSkipToSummaryButton ? (
                                <>
                                    <Button
                                        variant="borderPrimary"
                                        isDisabled={
                                            shouldButtonBeDisabled ||
                                            activeButton === 'skip'
                                        }
                                        onClick={handleContinue}
                                        isLoading={
                                            activeButton === 'continue' &&
                                            shouldButtonBeLoading
                                        }
                                        width={['100%', 'initial']}
                                        testId="step-button-next-step"
                                    >
                                        <FormattedMessage
                                            id="global.btn.next_step"
                                            defaultMessage="Następny krok"
                                        />
                                    </Button>
                                    <Button
                                        variant="primary"
                                        isDisabled={
                                            shouldButtonBeDisabled ||
                                            activeButton === 'continue'
                                        }
                                        onClick={handleSkipToSummary}
                                        isLoading={
                                            activeButton === 'skip' &&
                                            shouldButtonBeLoading
                                        }
                                        testId="step-button-goto-summary"
                                    >
                                        <FormattedMessage
                                            id="global.btn.go_to_summary"
                                            defaultMessage="Przejdź do podsumowania"
                                        />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="primary"
                                    isDisabled={shouldButtonBeDisabled}
                                    onClick={handleContinue}
                                    isLoading={shouldButtonBeLoading}
                                    testId="step-button-continue"
                                >
                                    <FormattedMessage
                                        id="global.btn.continue"
                                        defaultMessage="Kontynuuj"
                                    />
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            </Col>
        </Row>
    )
}

export default Step
